/* eslint-disable import/no-import-module-exports */
/* eslint-disable max-len */
/**
 * Create the store with asynchronously loaded reducers
 */

import { createStore, applyMiddleware, compose } from 'redux';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import { routerMiddleware } from 'react-router-redux';
import createSagaMiddleware from 'redux-saga';
import { attachXRayConfigurationWithLocalStorageSynchronizationObserver } from 'app/redux/xrayGeneratorConfiguration/reducer';
import { attachPACSConfigurationWithLocalStorageSynchronizationObserver } from 'app/redux/PACSConfiguration/reducer';
import { attachFlatPanelConfigurationWithLocalStorageSynchronizationObserver } from 'app/redux/flatPanelConfiguration/reducer';
import { attachGlobalSubscriptionsConfigurationWithLocalStorageSynchronizationObserver } from 'app/redux/global/reducer';
import { attachUserInputMappingWithLocalStorageSynchronizationObserver } from 'app/redux/userInputMapping/reducer';
import { attachAiOnlyConfigurationWithLocalStorageSynchronizationObserver } from 'app/redux/aiOnlyConfiguration/reducer';
import { attachDeviceParametersWithLocalStorageSynchronizationObserver } from 'app/redux/deviceParameters/reducer';
import languageMiddleware from 'app/containers/LanguageProvider/languageMiddleware';
import attachURLRedirectionManagerObserver from 'app/containers/LanguageProvider/urlRedirectionManager';
import { attachSubscriptionManagerObserver } from 'app/redux/subscriptionsManager';
import { getAsyncInjectors } from 'app/utils/asyncInjectors';
import loginSagas from 'app/redux/loginSagas';
import createReducer from 'app/redux/reducers';
import { attachSyncPMSStateWithLocalStorageObserver } from 'app/redux/pms/selector';
import { attachPermissionsWithLocalStorageSynchronizationObserver } from 'app/redux/permissions/reducer';
import { attachTeleradiologyWithLocalStorageSynchronizationObserver } from 'app/redux/teleradiology/reducer';
import { attachAnnouncementsWithLocalStorageSynchronizationObserver } from 'app/redux/announcements/reducer';
import createUserConfigurationAPIMiddleware from 'app/redux/middleware/SharedUserConfigMiddleware';
import { attachStorageWithLocalStorageSynchronizationObserver } from 'app/redux/storage/reducer';
import { attachConstructorConfigurationWithLocalStorageSynchronizationObserver } from 'app/redux/constructorConfiguration/reducer';
import { attachExportSettingsWithLocalStorageSynchronizationObserver } from 'app/redux/exportSettings/reducer';
import { attachViewerConfigurationWithLocalStorageSynchronizationObserver } from 'app/redux/viewerConfiguration/reducer';
import { attachProcessingWithLocalStorageSynchronizationObserver } from 'app/redux/processing/reducer';
import { attachRotationHistoryWithLocalStorageSynchronizationObserver } from 'app/redux/rotationHistory/reducer';
import { attachClinicInfoWithLocalStorageSynchronizationObserver } from 'app/redux/clinicInfo/reducer';

const sagaMiddleware = createSagaMiddleware();

export default function configureStore(initialState = {}, { history, sharedUserConfigAPI } = {}) {
  // Create the store with two middlewares
  // 1. sagaMiddleware: Makes redux-sagas work
  // 2. routerMiddleware: Syncs the location/URL path to the state
  const listenerMiddleware = createListenerMiddleware();
  const middlewares = [
    listenerMiddleware.middleware,
    sagaMiddleware,
    routerMiddleware(history),
    languageMiddleware,
    createUserConfigurationAPIMiddleware(sharedUserConfigAPI),
  ];

  const enhancers = [applyMiddleware(...middlewares)];

  // If Redux DevTools Extension is installed use it, otherwise use Redux compose
  /* eslint-disable no-underscore-dangle */
  const composeEnhancers =
    process.env.NODE_ENV !== 'production' &&
    typeof window === 'object' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;
  /* eslint-enable */

  const store = createStore(createReducer(), initialState, composeEnhancers(...enhancers));

  // Observers
  attachSubscriptionManagerObserver(store);
  attachSyncPMSStateWithLocalStorageObserver(store);
  attachURLRedirectionManagerObserver(store);
  attachViewerConfigurationWithLocalStorageSynchronizationObserver(store);
  attachXRayConfigurationWithLocalStorageSynchronizationObserver(store);
  attachPACSConfigurationWithLocalStorageSynchronizationObserver(store);
  attachFlatPanelConfigurationWithLocalStorageSynchronizationObserver(store);
  attachGlobalSubscriptionsConfigurationWithLocalStorageSynchronizationObserver(store);
  attachUserInputMappingWithLocalStorageSynchronizationObserver(store);
  attachAiOnlyConfigurationWithLocalStorageSynchronizationObserver(store);
  attachDeviceParametersWithLocalStorageSynchronizationObserver(store);
  attachPermissionsWithLocalStorageSynchronizationObserver(store);
  attachTeleradiologyWithLocalStorageSynchronizationObserver(store);
  attachAnnouncementsWithLocalStorageSynchronizationObserver(store);
  attachStorageWithLocalStorageSynchronizationObserver(store);
  attachConstructorConfigurationWithLocalStorageSynchronizationObserver(store);
  attachExportSettingsWithLocalStorageSynchronizationObserver(store);
  attachProcessingWithLocalStorageSynchronizationObserver(store);
  attachRotationHistoryWithLocalStorageSynchronizationObserver(store);
  attachClinicInfoWithLocalStorageSynchronizationObserver(store);
  // Extensions
  store.runSaga = sagaMiddleware.run;

  store.asyncReducers = {}; // Async reducer registry

  const { injectSagas } = getAsyncInjectors(store);

  // Global sagas
  injectSagas([loginSagas]);

  // Make reducers hot reloadable, see http://mxs.is/googmo
  /* istanbul ignore next */
  if (module.hot) {
    module.hot.accept('./redux/reducers', () => {
      import('./redux/reducers').then((reducerModule) => {
        const createReducers = reducerModule.default;
        const nextReducers = createReducers(store.asyncReducers);

        store.replaceReducer(nextReducers);
      });
    });
  }

  return store;
}
